import React, { useState } from 'react';
import { connectOwn } from '../../services/protectBuild';
import { EYEBROW, NOTE } from './typeScale';

/**
 * Whose connection does this agent run through?
 *
 * <h3>Why there is a choice</h3>
 *
 * A customer evaluating Protect has nothing configured yet and must still be
 * able to finish — that is what Prove7's own connection is for, within the
 * allowance their plan already carries. A customer who runs their own framework
 * must be able to point at it, and that goes through the platform's connector
 * API, exactly as the console does it, rather than through a shortcut invented
 * for this screen.
 *
 * <h3>Why a new connection is proved before it is bound</h3>
 *
 * An agent bound to a connection nobody has ever called is a registration that
 * succeeds and a run that fails days later in front of somebody else. So a
 * connection the customer adds is created and health-checked, and only a
 * connection that answered is handed back to be bound.
 */
export default function ConnectionAsk({ framework, allowance, onAnswer, busy = false }) {
  const provided = framework?.provided || [];
  const own = framework?.own || [];
  const listed = [
    ...provided.map((c) => ({ ...c, provided: true })),
    ...own.map((c) => ({ ...c, provided: false })),
  ];

  const [pickedId, setPickedId] = useState(listed[0]?.id || null);
  const [adding, setAdding] = useState(listed.length === 0);
  const [form, setForm] = useState({ name: '', endpointUrl: '', apiKey: '' });
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState(null);

  const picked = listed.find((c) => c.id === pickedId) || null;
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const allowanceLine = allowance
    ? `Included up to ${allowance.limit}${allowance.unit ? ` ${allowance.unit}` : ''}`
      + `${allowance.period ? ` per ${allowance.period}` : ''} on your plan.`
    : null;

  const add = async () => {
    setWorking(true);
    setProblem(null);
    try {
      const made = await connectOwn({
        platformType: framework?.platformType,
        name: form.name.trim(),
        endpointUrl: form.endpointUrl.trim(),
        apiKey: form.apiKey,
      });
      if (made.health !== 'HEALTHY') {
        /* Created, but it did not answer. It is not bound, and the reason is
           shown rather than a generic failure — the customer is the only one
           who can fix an endpoint or a key. */
        setProblem(made.message || `The connection answered ${made.health}.`);
        return;
      }
      onAnswer({ connectorId: made.id, label: made.name || form.name.trim(), provided: false,
        health: made.health });
    } catch (e) {
      setProblem(e?.message || 'The connection could not be created.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' }}>
      {listed.length === 0 ? (
        <p style={NOTE}>
          Nothing is connected for {framework?.label || 'this framework'} yet. Connect your own
          below, and we will check that it answers before anything is bound to it.
        </p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {listed.map((c) => {
            const on = c.id === pickedId;
            return (
              <button
                key={c.id}
                type="button"
                aria-pressed={on}
                onClick={() => { setPickedId(c.id); setAdding(false); }}
                style={{
                  textAlign: 'left', cursor: 'pointer', padding: '9px 13px',
                  borderRadius: 'var(--p7-radius-control)',
                  border: `1px solid ${on ? 'var(--p7-accent)' : 'var(--p7-line-strong)'}`,
                  background: on ? 'var(--p7-accent-bg)' : 'var(--p7-surface)',
                  font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                  color: 'var(--p7-ink)',
                }}
              >
                <span style={{ display: 'block', fontWeight: 600 }}>{c.name}</span>
                <span style={{ display: 'block', marginTop: '1px',
                               font: 'var(--p7-text-caption)',
                               fontFamily: 'var(--p7-font-sans)',
                               color: 'var(--p7-ink-subtle)' }}>
                  {c.provided ? 'Provided by Prove7' : 'Yours'}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {allowanceLine && provided.length > 0 ? <p style={NOTE}>{allowanceLine}</p> : null}

      {adding ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '9px',
                      paddingTop: 'var(--p7-space-2)' }}>
          <Field label="Name" value={form.name} onChange={set('name')}
                 placeholder="Our Claude" />
          <Field label="Address" value={form.endpointUrl} onChange={set('endpointUrl')}
                 placeholder="https://claude.acme.com" />
          <Field label="Key" value={form.apiKey} onChange={set('apiKey')} type="password"
                 placeholder="Paste its key" />
          {problem ? <p className="p7-note p7-note--deny" style={{ margin: 0 }}>{problem}</p> : null}
          <div style={{ display: 'flex', gap: '9px', alignItems: 'center' }}>
            <button type="button" className="p7-btn p7-btn--primary"
                    disabled={busy || working || !form.name.trim() || !form.endpointUrl.trim()}
                    onClick={add}>
              {working ? 'Connecting…' : 'Connect & test'}
            </button>
            {listed.length > 0 ? (
              <button type="button" className="p7-btn p7-btn--sm"
                      onClick={() => { setAdding(false); setProblem(null); }}>
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '9px', alignItems: 'center' }}>
          <button type="button" className="p7-btn p7-btn--primary" disabled={busy || !picked}
                  onClick={() => picked && onAnswer({
                    connectorId: picked.id, label: picked.name, provided: picked.provided,
                  })}>
            Use this connection
          </button>
          <button type="button" className="p7-btn p7-btn--sm" onClick={() => setAdding(true)}>
            Connect your own
          </button>
        </div>
      )}
    </div>
  );
}

function Field({ label, ...rest }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', marginBottom: '3px',
                     ...EYEBROW }}>
        {label}
      </span>
      <input
        {...rest}
        style={{ width: '100%', padding: '9px 12px',
                 border: '1px solid var(--p7-line-strong)',
                 borderRadius: 'var(--p7-radius-control)',
                 font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)' }}
      />
    </label>
  );
}

